import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Check, Clock } from 'lucide-react';
import { TIME_SINKS, PROGRAMS, SURVEY_INTRO, SURVEY_Q, MAX_HOURS, SHORT, OTHER_TEXT, surveyComplete, surveyProblem, problemQuestion } from '@/lib/editorTime';

/**
 * The one-time editor survey (lib/editorTime.js; backend app/editor_time.py), asked before an editor sees
 * their board. A PROMPT in the queue's terms (components/announcements/AnnouncementQueue.js): the product
 * needs the answer, so it blocks the board and comes back every visit until answered, and announcements,
 * the Song Approval intro among them, wait behind it. No close button, no Escape, no backdrop click, the way
 * the retired footage-intake prompt worked. The server keeps the answers with the editor's id and the date,
 * and never asks again.
 *
 * NO WAY OUT (Ryan, 2026-09-29: "they must answer"). A failed send keeps the card open with the reason, so the
 * editor can correct it or send again. Once it saves it never returns: the answer is kept on the user record
 * (time_survey.answered_at, in Mongo), which a restart or a deploy cannot touch, and nothing else writes it.
 */
const EMPTY = { typical_hours: '', hours_unreadable: false, time_sinks: [], time_sinks_other: '', wish: '', program: '', program_other: '' };

const Num = ({ n }) => (
  <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{n}</span>
);

// One question's block. `flagged` after a send that could not go: an ember outline around the question that is in
// the way, so an editor who scrolled past it can see which one it is. The border is always there (transparent) so
// flagging it moves nothing.
const Question = ({ n, flagged, qRef, children }) => (
  <div ref={qRef} data-testid={`time-survey-q-${n}`} data-flagged={flagged ? 'true' : 'false'}
    {...(flagged ? { 'data-tone': 'warning' } : {})}
    className={`flex gap-3 -m-2 p-2 rounded-lg border transition-colors ${
      flagged ? 'border-amber-500/60 bg-amber-500/[0.06]' : 'border-transparent'}`}>
    {children}
  </div>
);

const Choice = ({ on, onClick, round, testid, disabled, children }) => (
  <button type="button" role={round ? 'radio' : 'checkbox'} aria-checked={on} onClick={onClick} data-testid={testid}
    className={`tap-auto w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-colors ${
      on ? 'border-ug-gold/40 bg-ug-gold/[0.06]' : 'border-ug-border hover:border-ug-border-strong'} ${disabled ? 'opacity-50' : ''}`}>
    <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${round ? 'rounded-full' : 'rounded'} ${
      on ? 'bg-ug-gold border-ug-gold' : 'border-ug-border-strong'}`}>
      {on && <Check className="w-2.5 h-2.5 text-obsidian" strokeWidth={3} />}
    </span>
    <span className={`text-[12px] leading-snug ${on ? 'text-zinc-100' : 'text-zinc-100/75'}`}>{children}</span>
  </button>
);

export default function EditorTimeSurvey() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Set by pressing send before the answers are complete. Until then nothing is outlined: a card that opens
  // covered in warnings before anyone has typed is shouting at the wrong moment.
  const [tried, setTried] = useState(false);
  const cardRef = useRef(null);
  const qRefs = useRef({});

  useEffect(() => {
    if (user?.role !== 'editor') return;
    api.get('/editor/time-survey').then(r => { if (r.data?.owed) setOpen(true); }).catch(() => {});
  }, [user?.role]);

  // Truly blocking: Escape is swallowed and Tab stays inside the card.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const list = Array.from(cardRef.current.querySelectorAll('button, input, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter(n => !n.disabled && n.offsetParent !== null);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  }, [open]);

  const set = (k, v) => setA(p => ({ ...p, [k]: v }));
  // Exactly two: a third pick waits until one of the two is taken off.
  const toggleSink = (k) => setA(p => p.time_sinks.includes(k)
    ? { ...p, time_sinks: p.time_sinks.filter(x => x !== k) }
    : p.time_sinks.length >= 2 ? p : { ...p, time_sinks: [...p.time_sinks, k] });

  const ready = surveyComplete(a);
  const problem = surveyProblem(a);
  const hoursOver = Number(a.typical_hours) > MAX_HOURS;
  const blockedAt = problemQuestion(problem);
  const started = Object.entries(a).some(([k, v]) => k !== 'hours_unreadable' && (Array.isArray(v) ? v.length : `${v}`.trim()));
  const warn = !ready && (tried || started);
  // Ryan, 2026-09-30: the button used to go grey with the reason in small grey type under it, and he missed that
  // question 2 wanted two picks -- as the editors had. Now the button always answers: if something is missing it
  // takes you to that question, outlines it, and says what it needs in a warning under the button.
  const submit = async () => {
    if (saving) return;
    if (!ready) {
      setTried(true);
      const el = qRefs.current[blockedAt];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    try {
      const { hours_unreadable, ...answers } = a;
      await api.post('/editor/time-survey', { ...answers, typical_hours: Number(a.typical_hours) });
      setOpen(false);
      toast.success('Thank you. That helps us build the right tools.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Your answers did not save. Please try again.');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const q = 'text-[13px] text-zinc-100 font-medium leading-snug';
  return (
    <div className="ug-modal fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" data-testid="time-survey-prompt">
      <div ref={cardRef} role="dialog" aria-modal="true" aria-labelledby="time-survey-title"
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl">

        <div className="p-6 pb-5 border-b border-ug-border-subtle">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">Quick survey</span>
            <Clock className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
          </div>
          <h2 id="time-survey-title" data-testid="time-survey-title" className="tap-auto font-heading text-2xl text-zinc-100 mb-2">Where your time goes</h2>
          <p className="text-[13px] text-zinc-100/75 leading-relaxed" data-testid="time-survey-intro">{SURVEY_INTRO}</p>
        </div>

        <div className="p-6 py-5 flex flex-col gap-6">
          <Question n={1} flagged={tried && blockedAt === 1} qRef={el => { qRefs.current[1] = el; }}>
            <Num n={1} />
            <div className="flex-1 flex flex-col gap-2">
              <p className={q}>{SURVEY_Q.hours}</p>
              <div className="flex items-center gap-2">
                <Input type="number" inputMode="decimal" min="0.5" max={MAX_HOURS} step="0.5" value={a.typical_hours}
                  onChange={e => set('typical_hours', e.target.value)}
                  // onInput, not onChange: React fires onChange only when the value moves, and text the box cannot
                  // read ("abc", "6-8") leaves the value at '' -- so typing it into an empty box fired nothing.
                  onInput={e => set('hours_unreadable', !!e.currentTarget.validity?.badInput)}
                  data-testid="time-survey-hours"
                  className="tap-auto bg-ug-surface border-ug-border text-zinc-100 h-9 w-28 text-sm" />
                <span className="text-[12px] text-slate-500">hours</span>
              </div>
              {hoursOver && (
                <p className="text-[11px] text-ug-status-red" data-testid="time-survey-hours-error">
                  That is more than {MAX_HOURS} hours. Enter the hours for one highlight film.
                </p>
              )}
            </div>
          </Question>

          <Question n={2} flagged={tried && blockedAt === 2} qRef={el => { qRefs.current[2] = el; }}>
            <Num n={2} />
            <div className="flex-1 flex flex-col gap-2">
              <p className={q}>{SURVEY_Q.sinks}</p>
              {a.time_sinks.length === 1 ? (
                <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-amber-300 flex items-center gap-1"
                  data-testid="time-survey-sinks-count" data-tone="warning">
                  <AlertTriangle className="w-3 h-3" /> Pick one more (1 of 2 picked)
                </span>
              ) : (
                <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500" data-testid="time-survey-sinks-count">
                  Pick two{a.time_sinks.length ? ` (${a.time_sinks.length} picked)` : ''}
                </span>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {TIME_SINKS.map(([k, label]) => {
                  const on = a.time_sinks.includes(k);
                  return (
                    <Choice key={k} on={on} onClick={() => toggleSink(k)} testid={`time-survey-sink-${k}`}
                      disabled={!on && a.time_sinks.length >= 2}>{label}</Choice>
                  );
                })}
              </div>
              {a.time_sinks.includes('other') && (
                <Input value={a.time_sinks_other} onChange={e => set('time_sinks_other', e.target.value)} maxLength={OTHER_TEXT}
                  placeholder="What is the other part?" data-testid="time-survey-sink-other"
                  className="tap-auto bg-ug-surface border-ug-border text-zinc-100 h-9 text-sm" />
              )}
            </div>
          </Question>

          <Question n={3} flagged={tried && blockedAt === 3} qRef={el => { qRefs.current[3] = el; }}>
            <Num n={3} />
            <div className="flex-1 flex flex-col gap-2">
              <p className={q}>{SURVEY_Q.wish}</p>
              <textarea value={a.wish} onChange={e => set('wish', e.target.value)} rows={2} maxLength={SHORT}
                data-testid="time-survey-wish" placeholder="One thing, in a line or two"
                className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2.5 text-[13px] focus:border-ug-gold/40 outline-none resize-none" />
            </div>
          </Question>

          <Question n={4} flagged={tried && blockedAt === 4} qRef={el => { qRefs.current[4] = el; }}>
            <Num n={4} />
            <div className="flex-1 flex flex-col gap-2">
              <p className={q}>{SURVEY_Q.program}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" role="radiogroup" aria-label={SURVEY_Q.program}>
                {PROGRAMS.map(([k, label]) => (
                  <Choice key={k} round on={a.program === k} onClick={() => set('program', k)} testid={`time-survey-program-${k}`}>{label}</Choice>
                ))}
              </div>
              {a.program === 'other' && (
                <Input value={a.program_other} onChange={e => set('program_other', e.target.value)} maxLength={OTHER_TEXT}
                  placeholder="Which program?" data-testid="time-survey-program-other"
                  className="tap-auto bg-ug-surface border-ug-border text-zinc-100 h-9 text-sm" />
              )}
            </div>
          </Question>
        </div>

        <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20 rounded-b-2xl">
          {warn && (
            <div role="alert" data-testid="time-survey-hint" data-tone="warning"
              className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-500/50 bg-amber-500/[0.08] text-amber-300">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              <p className="text-[12px] leading-snug">
                <span className="font-semibold">Not ready to send yet.</span> {problem}
              </p>
            </div>
          )}
          <button onClick={submit} disabled={saving} data-testid="time-survey-submit" data-ready={ready ? 'true' : 'false'}
            className={`tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-90 disabled:cursor-wait ${
              ready ? '' : 'opacity-60'}`}>
            {saving ? 'Saving…' : 'Send my answers'}
          </button>
        </div>
      </div>
    </div>
  );
}
